import React, { useState, useEffect, useRef } from 'react'

const imgMoney03 = '/icon/money-03.svg'
const imgRectangle = '/icon/star.png'
const imgRectangle1 = '/icon/zap.png'
const imgRectangle2 = '/icon/shield.png'
const imgMessageMultiple01 = '/icon/message-multiple-01.svg'
const imgCustomerService02 = '/icon/customer-service-02 2.svg'
const imgWhatsapp = '/icon/whatsapp.svg'
const imgDashboardCircleEdit = '/icon/dashboard-circle-edit.svg'
const imgFile01 = '/icon/file-01.svg'
const imgArrowDown01 = '/icon/arrow-down-01.svg'
const imgArtificialIntelligence04 = '/icon/artificial-intelligence-04.svg'
const imgChart01 = '/icon/chart-01.svg'
const imgBot = '/icon/bot.svg'
const imgTeamManagement = '/icon/team-management.svg'
const imgMobileApps = '/icon/mobile-apps.svg'
const imgMaterialSymbolsPlugConnect = '/icon/material-symbols-plug-connect.svg'
const imgTimer02 = '/icon/timer-02.svg'
const imgLayoutTable02 = '/icon/layout-table-02.svg'
const imgAiAssistedReplies = '/icon/ai-assisted-replies.svg'

// Pricing tiers by contacts (matches pricing.astro)
const contactPricingTiers = [
  { numbers: 500, price: 49 },
  { numbers: 1000, price: 99 },
  { numbers: 1500, price: 139 },
  { numbers: 3000, price: 229 },
  { numbers: 4000, price: 259 },
  { numbers: 6000, price: 299 },
  { numbers: 8000, price: 379 },
  { numbers: 10000, price: 479 },
]

const BASIC_RATIO = 19 / 49 // keep Basic proportional to Pro base
const AGENCY_RATIO = 1.1 // Agency is +10% over Pro
const ANNUAL_FACTOR = 10 / 12 // 2 months free

// Price ID mappings by contact amount for Basic and Pro plans
const basicPriceIds = {
  500: 'price_1SeIpiDDMDdHoCjuw6extiCt', // 19 USD - Basic plan (no free trial)
  1000: 'price_1SeIpiDDMDdHoCjucJUp5ymV', // 38.39 USD - Basic plan (no free trial)
  1500: 'price_1SeIpiDDMDdHoCjuducv1pxj', // 53.90 USD - Basic plan (no free trial)
  3000: 'price_1SeIpiDDMDdHoCjuVdl024Ki', // 88.80 USD - Basic plan (no free trial)
}

const proPriceIds = {
  500: 'price_1SeJDnDDMDdHoCjuzi4UkIMA', // 49.00 USD
  1000: 'price_1SeJDnDDMDdHoCjuOyzOFLYy', // 99 USD
  1500: 'price_1SeJDnDDMDdHoCju6ktBtOZn', // 139 USD
  3000: 'price_1SeJDnDDMDdHoCjuj63MN16e', // 229 USD
}

const agencyPriceIds = {
  500: 'price_1SpDZEDDMDdHoCjuaEvPYqQr', // Agency 500 contacts
  1000: 'price_1SpDbuDDMDdHoCju0rmWgq1V', // Agency 1000 contacts
}

const DEFAULT_SIGNUP_URL =
  window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('localhost')
    ? 'http://localhost:4321/sign-up'
    : 'https://console.notifyer-systems.com/sign-up'

const featureIcons = {
  messages: imgMessageMultiple01,
  support: imgCustomerService02,
  whatsapp: imgWhatsapp,
  integrations: imgDashboardCircleEdit,
  files: imgFile01,
  automation: imgMaterialSymbolsPlugConnect,
  analytics: imgChart01,
  bot: imgBot,
  team: imgTeamManagement,
  mobile: imgMobileApps,
  timer: imgTimer02,
  crm: imgLayoutTable02,
  ai: imgAiAssistedReplies,
}

// Email Modal Component
const EmailModal = ({ isOpen, onClose, onSubmit }) => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      // Get email from URL params, cookies, or localStorage
      const urlParams = new URLSearchParams(window.location.search)
      const emailFromUrl = urlParams.get('email')
      if (emailFromUrl) {
        setEmail(emailFromUrl)
      } else {
        const cookies = document.cookie.split(';')
        for (const cookie of cookies) {
          const [name, value] = cookie.trim().split('=')
          if (name === 'email' && value) {
            setEmail(decodeURIComponent(value))
            break
          }
        }
        if (!email) {
          const storedEmail = localStorage.getItem('notifyer_user_email')
          if (storedEmail) {
            setEmail(storedEmail)
          }
        }
      }
      // Focus input after a short delay
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email || isLoading) return

    setIsLoading(true)
    try {
      // Store email in localStorage and cookie
      localStorage.setItem('notifyer_user_email', email)
      document.cookie = `email=${encodeURIComponent(email)}; path=/; max-age=${365 * 24 * 60 * 60}`

      await onSubmit(email)
      onClose()
    } catch (error) {
      console.error('Error submitting email:', error)
      alert('Failed to submit email. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h3 className="mb-2 text-xl font-semibold text-[#1c2329]">Start your free trial</h3>
        <p className="mb-6 text-sm text-[#656f68]">Enter your email to create your Notifyer workspace</p>

        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
            className="mb-4 w-full rounded-lg border border-[#e7e7e7] px-4 py-3 text-[#1c2329] placeholder:text-[#656f68] focus:border-[#01cd74] focus:outline-none"
          />
          <button
            type="submit"
            disabled={isLoading || !email}
            className="w-full rounded-full bg-[#1c2329] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#0f1720] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Loading...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  )
}

const Pricing = () => {
  const [billingMode, setBillingMode] = useState('monthly') // 'monthly' | 'annual'
  const [sliderValues, setSliderValues] = useState({
    Basic: 0,
    Pro: 0,
    Agency: 0,
  })
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingPlan, setLoadingPlan] = useState(null)
  const [isProExpanded, setIsProExpanded] = useState(false)

  // Get user email with priority: URL params > Cookies > localStorage
  const getUserEmail = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const emailFromUrl = urlParams.get('email')
    if (emailFromUrl) return emailFromUrl

    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === 'email' && value) {
        return decodeURIComponent(value)
      }
    }

    return localStorage.getItem('notifyer_user_email')
  }

  // Generate a very strong random password
  const generateStrongPassword = () => {
    const length = 32
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?'
    const values = crypto.getRandomValues(new Uint32Array(length))
    let password = ''
    for (let i = 0; i < length; i++) {
      password += charset[values[i] % charset.length]
    }
    return password
  }

  // Create user account
  const createUserAccount = async (email) => {
    try {
      const password = generateStrongPassword()

      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: '',
          email: email,
          password: password,
          phone_number: 0,
          reason_of_automate: 'I came from add for automation',
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))

        if (
          response.status === 403 &&
          (errorData.code === 'ERROR_CODE_ACCESS_DENIED' || errorData.message?.includes('already in use'))
        ) {
          return { exists: true }
        }

        console.error('Failed to create user account:', errorData)
        throw new Error('Failed to create user account')
      }

      const data = await response.json()
      const authToken = data.authToken || data.at || data.token

      if (!authToken) {
        throw new Error('No auth token in response')
      }

      return { at: authToken, password }
    } catch (error) {
      console.error('Error creating user account:', error)
      return null
    }
  }

  // Login with email (for existing users)
  const loginWithEmail = async (email) => {
    try {
      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/auth/login_with_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to login with email:', errorData)
        throw new Error('Failed to login with email')
      }

      const data = await response.json()
      const authToken = data.authToken || data.at || data.token

      if (!authToken) {
        throw new Error('No auth token in response')
      }

      return { at: authToken }
    } catch (error) {
      console.error('Error logging in with email:', error)
      return null
    }
  }

  // Send user credentials via email (non-critical)
  const sendUserCredentials = async (email, password) => {
    try {
      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/send_mail_user_credential', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password_text: password,
        }),
      })

      if (!response.ok) {
        console.warn('Failed to send credentials email (non-critical):', await response.text().catch(() => ''))
      }
    } catch (error) {
      console.warn('Error sending credentials email (non-critical):', error)
    }
  }

  // Get cookie value by name
  const getCookie = (name) => {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=')
      if (cookieName === name) {
        return decodeURIComponent(cookieValue || '')
      }
    }
    return null
  }

  // Extract URL parameters
  const extractURLParams = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const params = {}
    for (const [key, value] of urlParams.entries()) {
      params[key] = value
    }
    return params
  }

  // Get price ID for a plan and contact amount
  const getPriceId = (contactAmount, plan) => {
    const priceIdMap =
      plan === 'Basic'
        ? basicPriceIds
        : plan === 'Pro'
          ? proPriceIds
          : plan === 'Agency'
            ? agencyPriceIds
            : null
    return priceIdMap?.[contactAmount] || null
  }

  // Create Stripe checkout session
  const createCheckoutSession = async (priceId, plan) => {
    try {
      setIsLoading(true)
      setLoadingPlan(plan)

      const email = getUserEmail()
      if (!email) {
        setIsEmailModalOpen(true)
        // Keep loadingPlan set so we can retry after email is submitted
        return
      }

      // Create user account first (mandatory)
      let authToken
      let userResult = await createUserAccount(email)

      if (!userResult) {
        throw new Error('Failed to create user account')
      }

      // Check if user already exists
      if ('exists' in userResult && userResult.exists) {
        // User exists, login instead
        const loginResult = await loginWithEmail(email)
        if (!loginResult) {
          throw new Error('Failed to login with email')
        }
        authToken = loginResult.at

        // Send credentials email with "exist" as password (non-critical)
        await sendUserCredentials(email, 'exist')
      } else if ('at' in userResult && 'password' in userResult) {
        // New user created
        authToken = userResult.at

        // Send credentials email (non-critical)
        await sendUserCredentials(email, userResult.password)
      } else {
        throw new Error('Unexpected user result format')
      }

      // Get URL parameters
      const urlParams = extractURLParams()

      // Get datafast cookies
      const datafastVisitorId = getCookie('datafast_visitor_id')
      const datafastSessionId = getCookie('datafast_session_id')

      // Build success URL: Stripe redirects to ThankYou page on the landing site (with auth token for console login) (PREVIOUS FLOW).
      // Previously: redirected to console sign-in
      // const baseSuccessUrl =
      //   window.location.hostname === 'localhost' ||
      //     window.location.hostname === '127.0.0.1' ||
      //     window.location.hostname.includes('localhost')
      //     ? 'http://localhost:4321/sign-in'
      //     : 'https://console.notifyer-systems.com/sign-in'

      
      // Current: Stripe redirects to the landing site's ThankYou page after successful checkout.
      // We use origin so it works in both dev (e.g. localhost:5173) and prod. The `at` token
      // is passed in the URL so the ThankYou page can use it (e.g. for "Continue with setup" → console login) (CURRENT FLOW).
      const baseSuccessUrl = `${window.location.origin}/thank-you`
      const successUrl = `${baseSuccessUrl}?at=${encodeURIComponent(authToken)}`

      // Call the API
      const response = await fetch('https://api.insightssystem.com/api:Mk_r6mq0/session_from_frontend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        body: JSON.stringify({
          mode: 'subscription',
          price_id: priceId,
          success_url: successUrl,
          email: email,
          url_params: urlParams,
          datafast_visitor_id: datafastVisitorId || null,
          datafast_session_id: datafastSessionId || null,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }))
        console.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          priceId: priceId,
          plan: plan,
        })
        throw new Error(
          errorData.message || errorData.error || `Failed to create checkout session: ${response.status} ${response.statusText}`
        )
      }

      const data = await response.json()
      console.log('Checkout session response:', data)

      // Open the checkout session URL in the same window
      if (data.response?.result?.url) {
        window.location.href = data.response.result.url
      } else {
        console.error('Unexpected response structure:', data)
        throw new Error('No checkout URL in response')
      }
    } catch (error) {
      console.error('Error creating checkout session:', {
        error: error,
        message: error.message,
        stack: error.stack,
        priceId: priceId,
        plan: plan,
      })
      alert(`Failed to start checkout: ${error.message || 'Please try again.'}`)
      setIsLoading(false)
      setLoadingPlan(null)
    }
  }

  // Handle CTA button click
  const handleCtaClick = async (plan) => {
    if (isLoading) return

    const tierIndex = sliderValues[plan]
    const tier = contactPricingTiers[tierIndex] || contactPricingTiers[0]
    const contactAmount = tier.numbers

    // Agency plan: For annual billing or contact amounts without price IDs, redirect to TidyCal
    if (plan === 'Agency') {
      const TIDYCAL_URL = 'https://tidycal.com/axelmeta/notifyer-by-whatsable-demo-via-ads?embed=1'
      const agencyContactAmountsWithoutPriceIds = [1500, 3000, 4000, 6000, 8000, 10000]

      if (billingMode === 'annual' || agencyContactAmountsWithoutPriceIds.includes(contactAmount)) {
        window.location.href = TIDYCAL_URL
        return
      }
    }

    // For annual billing (Basic and Pro), redirect to signup
    if (billingMode === 'annual') {
      window.location.href = DEFAULT_SIGNUP_URL
      return
    }

    const priceId = getPriceId(contactAmount, plan)

    console.log('Checkout attempt:', { plan, contactAmount, tierIndex, priceId })

    if (priceId) {
      await createCheckoutSession(priceId, plan)
    } else {
      console.warn('No price ID found, redirecting to signup:', { plan, contactAmount })
      window.location.href = DEFAULT_SIGNUP_URL
    }
  }

  // Handle email modal submit
  const handleEmailSubmit = async (email) => {
    // Email is already stored in the modal, just close it
    // The checkout will use getUserEmail() which will get it from storage
    // If there's a pending plan, retry the checkout
    if (loadingPlan) {
      const tierIndex = sliderValues[loadingPlan]
      const tier = contactPricingTiers[tierIndex] || contactPricingTiers[0]
      const contactAmount = tier.numbers
      const priceId = getPriceId(contactAmount, loadingPlan)

      if (priceId) {
        await createCheckoutSession(priceId, loadingPlan)
      }
    }
  }

  // Calculate price for a plan based on tier and billing mode
  const calculatePrice = (plan, tierIndex) => {
    const tier = contactPricingTiers[tierIndex] || contactPricingTiers[0]
    const proMonthly = tier.price
    const proAnnual = proMonthly * ANNUAL_FACTOR
    const basicMonthly = proMonthly * BASIC_RATIO
    const basicAnnual = basicMonthly * ANNUAL_FACTOR
    const agencyMonthly = proMonthly * AGENCY_RATIO
    const agencyAnnual = agencyMonthly * ANNUAL_FACTOR

    let price = 0
    if (plan === 'Basic') {
      price = billingMode === 'monthly' ? basicMonthly : basicAnnual
    } else if (plan === 'Pro') {
      price = billingMode === 'monthly' ? proMonthly : proAnnual
    } else if (plan === 'Agency') {
      price = billingMode === 'monthly' ? agencyMonthly : agencyAnnual
    }

    const decimals = price % 1 === 0 ? 0 : 2
    return price.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  }

  // Get contact count for a plan based on tier
  const getContactCount = (tierIndex) => {
    const tier = contactPricingTiers[tierIndex] || contactPricingTiers[0]
    return tier.numbers.toLocaleString()
  }

  // Calculate slider position and fill width
  const getSliderStyles = (tierIndex) => {
    const maxIndex = 7 // 8 tiers (0-7) displayed
    // Calculate position to align with the center of each number column
    // Each of the 8 columns is 12.5% wide (100% / 8)
    // The center of each column is at: columnIndex * 12.5% + 6.25%
    // For tierIndex 0: 0 * 12.5% + 6.25% = 6.25%
    // For tierIndex 7: 7 * 12.5% + 6.25% = 93.75%
    const columnWidth = 100 / 8 // 12.5%
    const columnCenter = columnWidth / 2 // 6.25%
    const position = (tierIndex * columnWidth) + columnCenter
    const fillWidth = `${position}%`
    return { fillWidth, position: `${position}%` }
  }

  const plans = [
    {
      name: 'Basic',
      desc: 'Ideal if you only need to send bulk WhatsApp campaigns',
      messages: 'Unlimited bulk messaging campaigns',
      cta: 'Get Started',
      ctaStyle: 'outline',
      highlight: false,
      icon: imgRectangle,
    },
    {
      name: 'Pro',
      desc: 'Complete solution for teams needing automation, collaboration, and advanced analytics capabilities.',
      messages: 'Unlimited messages',
      cta: 'Start Free Trial',
      ctaStyle: 'solid',
      highlight: true,
      icon: imgRectangle1,
    },
    {
      name: 'Agency',
      desc: 'Built for agencies managing multiple client accounts and multiple WhatsApp Business numbers.',
      messages: '5 messages',
      cta: 'Get Started',
      ctaStyle: 'outline',
      highlight: false,
      icon: imgRectangle2,
    },
  ]

  return (
    <>
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false)
          // Reset loading state if modal is closed without submitting
          if (isLoading) {
            setIsLoading(false)
            setLoadingPlan(null)
          }
        }}
        onSubmit={handleEmailSubmit}
      />

      <section id="pricing" className="relative scroll-mt-20 bg-white px-4 py-10 sm:px-8 sm:py-12 lg:px-[100px] lg:py-16">
        <div className="mx-auto flex max-w-[1240px] flex-col items-center gap-10 sm:gap-12 text-center">
          {/* Pill */}
          <div className="flex items-center gap-2 rounded-full bg-[#fafafa] px-4 py-2 text-xs font-medium uppercase tracking-[0.28px] text-[#1c2329] shadow-[inset_0px_1px_1px_rgba(26,32,50,0.1),inset_0px_1px_2px_rgba(26,32,50,0.05)]">
            <img src={imgMoney03} alt="" className="h-6 w-6" />
            <span>Pricing</span>
          </div>

          <div className="space-y-2 px-1 sm:px-0 flex flex-col items-center">
            <h2 className="text-[28px] font-semibold leading-tight tracking-[-1.1px] text-[#1c2329] sm:text-[38px] md:text-[48px] lg:text-[56px]">
              Simple, transparent pricing
            </h2>
            <p className="max-w-[540px] text-[16px] leading-[1.7] tracking-[-0.5px] text-[#656f68] sm:text-[18px]">
              Choose the plan that fits your business needs. Start with a free trial and scale as you grow.
            </p>
          </div>

          {/* Billing Toggle - Sticky on mobile */}
          <div className="sticky top-[73px] sm:top-[81px] z-[60] flex w-fit items-center justify-center gap-2 mx-auto rounded-full border border-[#e7e7e7] bg-white p-1 shadow-sm md:static md:top-auto md:z-auto">
            <button
              onClick={() => setBillingMode('monthly')}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition sm:px-5 sm:text-sm ${billingMode === 'monthly'
                ? 'bg-[#1c2329] text-white'
                : 'text-[#656f68] hover:text-[#1c2329]'
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingMode('annual')}
              className={`relative rounded-full px-4 py-2 text-xs font-semibold transition sm:px-5 sm:text-sm ${billingMode === 'annual'
                ? 'bg-[#1c2329] text-white'
                : 'text-[#656f68] hover:text-[#1c2329]'
                }`}
            >
              Annual (2 months free)
            </button>
          </div>

          <div className="w-full rounded-2xl border border-[#f2f3f3] bg-[#fafaf9] p-3 sm:p-4">
            <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3">
              {plans.map((plan) => {
                const tierIndex = sliderValues[plan.name]
                const price = calculatePrice(plan.name, tierIndex)
                const contactCount = getContactCount(tierIndex)
                const sliderStyles = getSliderStyles(tierIndex)
                const isPlanLoading = isLoading && loadingPlan === plan.name

                return (
                  <div
                    key={plan.name}
                    className={`relative flex h-full flex-col overflow-hidden rounded-[10px] border bg-white p-6 sm:p-7 lg:p-8 ${plan.highlight
                      ? 'border-[#24d366] shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.1),0px_2px_4px_-2px_rgba(16,24,40,0.06)]'
                      : 'border-[#f3f3f2] shadow-[0px_1px_3px_rgba(0,0,0,0.04)]'
                      }`}
                  >
                    {plan.highlight && (
                      <div className="absolute inset-0 bg-gradient-to-b from-[#f6fff7] via-white to-[#e6fbee]" aria-hidden />
                    )}

                    <div className="relative z-10 flex h-full flex-col text-left">
                      <div className="grid grid-cols-[auto_1fr] items-start gap-3 h-[100px] sm:h-[110px]">
                        <img src={plan.icon} alt={`${plan.name} icon`} className="h-12 w-12 object-contain sm:h-14 sm:w-14 flex-shrink-0" />
                        <div className="space-y-2 flex-1 min-w-0">
                          <h3 className="text-[20px] font-semibold tracking-[-0.6px] text-[#1c2329] sm:text-[22px]">{plan.name}</h3>
                          <p className="text-[15px] leading-[1.6] text-[#656f68] sm:text-[16px]">{plan.desc}</p>
                        </div>
                      </div>

                      {/* Spacer to match the gap after the CTA */}
                      <div className="h-10 sm:h-12" aria-hidden />

                      {/* Price + slider block */}
                      <div className="space-y-4 sm:space-y-5">
                        <div className="grid grid-cols-[auto_1fr] items-end gap-3">
                          <span className="text-[32px] font-semibold tracking-[-1.1px] text-[#1c2329] sm:text-[40px]">
                            ${price}
                          </span>
                          <span className="pb-[6px] text-[14px] text-[#656f68] sm:text-[16px]">
                            per {billingMode === 'monthly' ? 'month' : 'year'}
                          </span>
                        </div>

                        <div className="space-y-3">
                          <div className="grid grid-cols-[1fr_auto] items-center text-[15px] font-normal text-[#1c2329] sm:text-[16px]">
                            <span>Contacts</span>
                            <span className="grid grid-cols-[auto_1fr] items-center gap-1 text-[#1c2329]">
                              <div className="h-[5px] w-[5px] rounded-full bg-[#1c2329]" />
                              {contactCount}
                            </span>
                          </div>
                          <div className="relative h-[12px] w-full rounded-full border border-[#f3f3f2] bg-[#e7e7e7] px-[2px] sm:h-[14px] overflow-visible">
                            {/* Interactive input - must be on top and cover full area with extended clickable zone */}
                            <input
                              type="range"
                              min="0"
                              max={contactPricingTiers.length - 1}
                              step="1"
                              value={tierIndex}
                              onChange={(e) => {
                                const newValue = parseInt(e.target.value)
                                setSliderValues((prev) => ({
                                  ...prev,
                                  [plan.name]: newValue,
                                }))
                              }}
                              onInput={(e) => {
                                const newValue = parseInt(e.target.value)
                                setSliderValues((prev) => ({
                                  ...prev,
                                  [plan.name]: newValue,
                                }))
                              }}
                              className="absolute -top-2 -bottom-2 left-0 right-0 w-full cursor-pointer appearance-none z-20"
                              style={{
                                background: 'transparent',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                padding: 0,
                                margin: 0,
                                height: 'calc(100% + 16px)',
                                opacity: 0.01,
                                pointerEvents: 'auto',
                                touchAction: 'pan-y',
                              }}
                            />
                            {/* Visual fill bar */}
                            <div
                              className="absolute left-[6px] top-1/2 h-[7px] -translate-y-1/2 rounded-full shadow-[0px_2px_2px_rgba(0,0,0,0.12)] transition-all sm:h-[8px] pointer-events-none z-0"
                              style={{
                                width: sliderStyles.fillWidth,
                                backgroundImage: plan.highlight
                                  ? 'linear-gradient(90deg, #3bea9e 0%, #01cd74 100%)'
                                  : 'linear-gradient(180deg, #4a5864 0%, #1c2329 92%)',
                              }}
                            />
                            {/* Background shadow overlay */}
                            <div className="absolute inset-0 rounded-full shadow-[inset_0px_1px_1px_rgba(26,32,50,0.1),inset_0px_1px_2px_rgba(26,32,50,0.05)] pointer-events-none z-0" />
                            {/* Visual knob */}
                            <div
                              className={`absolute top-1/2 size-5 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 border-white bg-gradient-to-b transition-all sm:size-6 pointer-events-none z-10 ${plan.highlight
                                ? 'from-[#3bea9e] to-[#01cd74] shadow-[0px_6px_8px_rgba(1,205,116,0.2)]'
                                : 'from-[#4a5864] to-[#1c2329] shadow-[0px_6px_8px_rgba(28,35,41,0.2)]'
                                }`}
                              style={{ left: sliderStyles.position }}
                            />
                          </div>
                          <div className="grid grid-cols-8 gap-0 text-[10px] text-[#656f68] sm:text-xs">
                            {contactPricingTiers.slice(0, 8).map((tier, idx) => (
                              <span key={idx} className="text-center">
                                {tier.numbers >= 1000 ? `${tier.numbers / 1000}k` : tier.numbers}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* CTA button with matching spacer below */}
                      <button
                        onClick={() => handleCtaClick(plan.name)}
                        disabled={isLoading}
                        className={`mt-10 w-full rounded-full px-8 py-3.5 text-[15px] font-semibold tracking-[-0.46px] transition sm:px-10 sm:py-4 sm:text-[16px] sm:mt-12 disabled:opacity-50 disabled:cursor-not-allowed ${plan.ctaStyle === 'solid'
                          ? 'bg-[#1c2329] text-white shadow-[0px_4px_8px_rgba(36,196,211,0.1)] hover:bg-[#0f1720]'
                          : 'border border-[#1c2329] text-[#1c2329] shadow-[0px_4px_8px_rgba(36,196,211,0.1)] hover:bg-[#fafafa]'
                          }`}
                      >
                        {isPlanLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Loading...
                          </span>
                        ) : (
                          plan.cta
                        )}
                      </button>

                      {/* Spacer matching the one before price */}
                      <div className="h-10 sm:h-12" aria-hidden />

                      <div className="space-y-4">
                        <p className="text-[17px] font-medium tracking-[-0.52px] text-[#1c2329] sm:text-[18px]">Features included:</p>
                        <div className="space-y-4 text-[15px] leading-[1.6] text-[#1c2329] sm:text-[16px]">
                          {plan.name === 'Agency' ? (
                            <>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.messages} alt="" className="h-5 w-5" />
                                <span>All Pro features included</span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.whatsapp} alt="" className="h-5 w-5" />
                                <span>Connect and manage <span className="text-[#656f68]">multiple phone numbers per subscription</span></span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.support} alt="" className="h-5 w-5" />
                                <span>Priority support with <span className="text-[#656f68]">dedicated account management</span></span>
                              </div>
                            </>
                          ) : plan.name === 'Pro' ? (
                            <>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.messages} alt="" className="h-5 w-5" />
                                <span>
                                  Unlimited automations &amp; webhooks <span className="text-[#656f68]">(e.g., Facebook Leads Ads send automatic WhatsApp messages and follow-ups)</span>
                                </span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.whatsapp} alt="" className="h-5 w-5" />
                                <span>
                                  Use existing WhatsApp <span className="text-[#656f68]">Business &amp; import chats</span>
                                </span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="h-5 w-5 text-[#1c2329]">
                                  <g fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinejoin="round" d="M7 6V5c0-1.414 0-2.121.44-2.56C7.878 2 8.585 2 10 2h4c1.414 0 2.121 0 2.56.44C17 2.878 17 3.585 17 5v1c0 1.414 0 2.121-.44 2.56C16.122 9 15.415 9 14 9h-1l-3 2V9c-1.414 0-2.121 0-2.56-.44C7 8.122 7 7.415 7 6Z" />
                                    <path d="M3.59 18.74c-.629.422-2.277 1.282-1.273 2.358c.49.526 1.037.902 1.723.902h3.92c.686 0 1.233-.376 1.723-.902c1.004-1.076-.644-1.936-1.273-2.357a4.29 4.29 0 0 0-4.82 0Zm12 0c-.629.422-2.277 1.282-1.273 2.358c.49.526 1.037.902 1.723.902h3.92c.686 0 1.233-.376 1.723-.902c1.004-1.076-.644-1.936-1.273-2.357a4.29 4.29 0 0 0-4.82 0ZM8 13.5a2 2 0 1 1-4 0a2 2 0 0 1 4 0Zm12 0a2 2 0 1 1-4 0a2 2 0 0 1 4 0Z" />
                                  </g>
                                </svg>
                                <span>Two-way conversations <span className="text-[#656f68]">and replies</span></span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.integrations} alt="" className="h-5 w-5" />
                                <span>
                                  Integrates <span className="text-[#656f68]">with Zapier, Make, n8n, REST API</span>
                                </span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-[#1c2329]">
                                  <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5">
                                    <path d="M20 11s0-1.57-.152-1.937s-.441-.657-1.02-1.235l-4.736-4.736c-.499-.499-.748-.748-1.058-.896a2 2 0 0 0-.197-.082C12.514 2 12.161 2 11.456 2c-3.245 0-4.868 0-5.967.886a4 4 0 0 0-.603.603C4 4.59 4 6.211 4 9.456V14c0 3.771 0 5.657 1.172 6.828S8.229 22 12 22m1-19.5V3c0 2.828 0 4.243.879 5.121C14.757 9 16.172 9 19 9h.5" />
                                    <path d="M20 19.407v-2.814c0-1.572 0-2.359-.54-2.554c-.54-.196-1.175.36-2.446 1.472L16.5 16h-1.496c-.943 0-1.414 0-1.707.293s-.293.764-.293 1.707s0 1.414.293 1.707s.764.293 1.707.293H16.5l.514.489c1.27 1.112 1.906 1.668 2.446 1.472s.54-.982.54-2.554" />
                                  </g>
                                </svg>
                                <span>Incoming audio messages are automatically transcribed</span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.files} alt="" className="h-5 w-5" />
                                <span>
                                  Send and receive PDFs<span className="text-[#656f68]">, photos, videos, attachments</span>
                                </span>
                              </div>
                              {isProExpanded && (
                                <>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <img src={featureIcons.analytics} alt="" className="h-5 w-5" />
                                    <span>
                                      Advanced analytics<span className="text-[#656f68]">: delivery, open, response, conversion</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" className="h-5 w-5 text-[#1c2329]">
                                      <g fill="currentColor">
                                        <path d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855a.75.75 0 0 0-.124 1.329l4.995 3.178l1.531 2.406a.5.5 0 0 0 .844-.536L6.637 10.07l7.494-7.494l-1.895 4.738a.5.5 0 1 0 .928.372zm-2.54 1.183L5.93 9.363L1.591 6.602z" />
                                        <path d="M16 12.5a3.5 3.5 0 1 1-7 0a3.5 3.5 0 0 1 7 0m-1.993-1.679a.5.5 0 0 0-.686.172l-1.17 1.95l-.547-.547a.5.5 0 0 0-.708.708l.774.773a.75.75 0 0 0 1.174-.144l1.335-2.226a.5.5 0 0 0-.172-.686" />
                                      </g>
                                    </svg>
                                    <span>
                                      Smart delivery system <span className="text-[#656f68]">for bulk campaigns</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <img src={featureIcons.bot} alt="" className="h-5 w-5" />
                                    <span>
                                      Smart bot delay system <span className="text-[#656f68]">to prevent spam and improve deliverability</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <img src={featureIcons.timer} alt="" className="h-5 w-5" />
                                    <span>
                                      Scheduled messages <span className="text-[#656f68]">+ automated follow-ups</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="h-5 w-5 text-[#1c2329]">
                                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 3.75a3 3 0 1 0 6 0a3 3 0 0 0-6 0m8.25 9.75a5.25 5.25 0 1 0-10.5 0v2.25H3l.75 7.5h4.5l.75-7.5h2.25zm4.875-10.125a2.625 2.625 0 1 0 5.25 0a2.625 2.625 0 0 0-5.25 0M14.25 11.25a4.5 4.5 0 1 1 9 0zm-3.512 0H23.25" />
                                    </svg>
                                    <span>
                                      Mini CRM<span className="text-[#656f68]">: Organize conversations with labels, notes, and tags for better follow-up and team collaboration</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="h-5 w-5 text-[#1c2329]">
                                      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" strokeWidth="1.5" d="m14.04 14.863l-.886 3.099c-.332 1.16-1.976 1.16-2.308 0l-.885-3.099a1.2 1.2 0 0 0-.824-.824l-3.099-.885c-1.16-.332-1.16-1.976 0-2.308l3.099-.885a1.2 1.2 0 0 0 .824-.824l.885-3.099c.332-1.16 1.976-1.16 2.308 0l.885 3.099a1.2 1.2 0 0 0 .824.824l3.099.885c1.16.332 1.16 1.976 0 2.308l-3.099.885a1.2 1.2 0 0 0-.824.824m5.531 4.853l-.376 1.508c-.05.202-.337.202-.388 0l-.377-1.508a.2.2 0 0 0-.145-.145l-1.508-.377c-.202-.05-.202-.337 0-.388l1.508-.377a.2.2 0 0 0 .145-.145l.377-1.508c.05-.202.337-.202.388 0l.377 1.508a.2.2 0 0 0 .145.145l1.508.377c.202.05.202.337 0 .388l-1.508.377a.2.2 0 0 0-.145.145m-14.001-14l-.376 1.508c-.05.202-.338.202-.388 0l-.377-1.508a.2.2 0 0 0-.145-.145l-1.508-.377c-.202-.05-.202-.338 0-.388l1.508-.377a.2.2 0 0 0 .145-.145l.377-1.508c.05-.202.338-.202.388 0l.377 1.508a.2.2 0 0 0 .145.145l1.508.377c.202.05.202.338 0 .388l-1.508.377a.2.2 0 0 0-.145.145" />
                                    </svg>
                                    <span>
                                      AI-assisted replies <span className="text-[#656f68]">or manual responses</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="h-5 w-5 text-[#1c2329]">
                                      <path fill="currentColor" d="M5.5 7a2.5 2.5 0 1 1 5 0a2.5 2.5 0 0 1-5 0M8 3a4 4 0 1 0 0 8a4 4 0 0 0 0-8m7.5 5a1.5 1.5 0 1 1 3 0a1.5 1.5 0 0 1-3 0M17 5a3 3 0 1 0 0 6a3 3 0 0 0 0-6M4.25 13A2.25 2.25 0 0 0 2 15.25v.278a2 2 0 0 0 .014.208a4.5 4.5 0 0 0 .778 2.07C3.61 18.974 5.172 20 8 20c1.369 0 2.441-.24 3.276-.62a6.5 6.5 0 0 1-.268-1.55c-.645.38-1.595.67-3.008.67c-2.422 0-3.486-.85-3.98-1.555a3 3 0 0 1-.513-1.359l-.007-.079v-.257a.75.75 0 0 1 .75-.75h7.482c.249-.477.555-.919.909-1.317A2.2 2.2 0 0 0 11.75 13zM23 17.5a5.5 5.5 0 1 1-11 0a5.5 5.5 0 0 1 11 0m-4.945-3.08a.577.577 0 0 0-1.11 0l-.557 1.788h-1.803c-.566 0-.8.754-.343 1.1l1.458 1.105l-.557 1.787c-.175.561.441 1.028.899.681l1.458-1.104l1.458 1.104c.458.347 1.074-.12.899-.68l-.557-1.788l1.458-1.104c.458-.347.223-1.101-.343-1.101h-1.803z" />
                                    </svg>
                                    <span>
                                      Multi-user roles, permissions, and admin chat <span className="text-[#656f68]">— assign specific chats to specific people in your team for best organization and collaboration</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <img src={featureIcons.bot} alt="" className="h-5 w-5" />
                                    <span>
                                      AI chatbots <span className="text-[#656f68]">&amp; AI/Human handoff</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32" className="h-5 w-5 text-[#1c2329]">
                                      <path fill="currentColor" d="M10.25 2A3.25 3.25 0 0 0 7 5.25v21.5A3.25 3.25 0 0 0 10.25 30h11.5A3.25 3.25 0 0 0 25 26.75V5.25A3.25 3.25 0 0 0 21.75 2zM9 5.25C9 4.56 9.56 4 10.25 4h11.5c.69 0 1.25.56 1.25 1.25v21.5c0 .69-.56 1.25-1.25 1.25h-11.5C9.56 28 9 27.44 9 26.75zM14 24a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2z" />
                                    </svg>
                                    <span>
                                      Dedicated iOS/Android apps <span className="text-[#656f68]">with push notifications by label &amp; role</span>
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                    <img src={featureIcons.support} alt="" className="h-5 w-5" />
                                    <span>
                                      Priority support<span className="text-[#656f68]">: Email, WhatsApp, Zoom (90% response in minutes)</span>
                                    </span>
                                  </div>
                                </>
                              )}
                              <div className="flex justify-center">
                                <button
                                  onClick={() => setIsProExpanded(!isProExpanded)}
                                  className="grid grid-cols-[auto_auto] items-center justify-items-center gap-1 text-[16px] font-medium text-[#1c2329] hover:opacity-70 transition-opacity cursor-pointer"
                                >
                                  <span>{isProExpanded ? 'View Less' : 'View More'}</span>
                                  <img
                                    src={imgArrowDown01}
                                    alt=""
                                    className={`h-5 w-5 transition-transform duration-200 ${isProExpanded ? 'rotate-180' : ''}`}
                                  />
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.messages} alt="" className="h-5 w-5" />
                                <span>
                                  {plan.messages.includes('messaging campaigns') ? (
                                    <>
                                      {plan.messages.split(' messaging campaigns')[0]} <span className="text-[#656f68]">messaging campaigns</span>
                                    </>
                                  ) : (
                                    plan.messages
                                  )}
                                </span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.whatsapp} alt="" className="h-5 w-5" />
                                <span>
                                  Send from your own <span className="text-[#656f68]">WhatsApp Business Number</span>
                                </span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="h-5 w-5 text-[#1c2329]">
                                  <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233a14 14 0 0 0 6.392 6.384" />
                                </svg>
                                <span>
                                  Keep your existing WhatsApp Business App <span className="text-[#656f68]">with your number</span>
                                </span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                                <img src={featureIcons.files} alt="" className="h-5 w-5" />
                                <span>
                                  Send PDFs<span className="text-[#656f68]">, photos, videos, attachments</span>
                                </span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] items-center gap-2 text-[#656f68]">
                                <img src={featureIcons.support} alt="" className="h-5 w-5" />
                                <span>
                                  <span className="text-[#1c2329]">Dedicated support</span> included
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <p className="mt-4 text-center text-[10px] text-[#656f68] sm:text-[11px]">
              WhatsApp messaging fees are billed separately by Meta.
            </p>
          </div>
        </div>
      </section>
    </>
  )
}

export default Pricing

